import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import React, { useState } from "react";
import useDebounce from "../../hooks/useDebounce";
import { distinct } from "../../utilities/arrayHelper";
import type { Validator } from "../../utilities/validators";

interface ChipsMultilineInputProps {
  onChange: (values: string[]) => void;
  values: string[];
  label?: string;
  placeholder?: string;
  inputValidator?: Validator<string>;
  sanitizeValue?: (value: string) => string;
  availableValues?: string[];
  freeSolo?: boolean;
  removeDuplicatesOnPaste?: boolean;
  restrictedItems?: string[];
  restrictedItemsHelperText?: string;
  disabled?: boolean;
}

const ChipsMultilineInput = ({
  onChange,
  values,
  label,
  placeholder,
  inputValidator,
  sanitizeValue,
  availableValues,
  freeSolo,
  removeDuplicatesOnPaste,
  restrictedItems,
  restrictedItemsHelperText,
  disabled,
}: ChipsMultilineInputProps) => {
  const [inputValue, setInputValue] = useState("");

  const clearInputText = useDebounce(() => {
    setInputValue("");
  }, 10);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", "Tab", ",", " "].includes(event.key)) {
      event.preventDefault();
      convertTextToChips();
    }
  };

  const sanitize = (value: string) => (sanitizeValue ? sanitizeValue(value) : value.trim());

  const convertTextToChips = () => {
    const newInputValue = sanitize(inputValue);
    if (newInputValue && validateInputValue(newInputValue)) {
      onChange([...values, newInputValue.toLocaleLowerCase()]);
      clearInputText();
    }
  };

  const handleDeleteChip = (_valueToDelete: string, index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  const validateInputValue = (valueInput: string) => {
    const value = valueInput.trim();
    if (!value) {
      return false;
    }
    if (inputValidator && !inputValidator(value).isValid) {
      return false;
    }
    if (availableValues && !freeSolo && !availableValues.includes(value)) {
      return false;
    }
    if (values.includes(value)) {
      return false;
    }
    return true;
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const pastedText = event.clipboardData.getData("text");
    const newValues = pastedText
      .split(/[,\s]+/)
      .map((value) => sanitize(value))
      .filter((value) => validateInputValue(value))
      .map((value) => value.toLocaleLowerCase());

    if (newValues.length) {
      if (removeDuplicatesOnPaste) {
        onChange([...values, ...distinct(newValues)]);
      } else {
        onChange([...values, ...newValues]);
      }

      clearInputText();
    }
  };

  const anyDuplicatesExist = new Set(values).size !== values.length;
  const anyRestrictedValuesExist =
    restrictedItems !== undefined && values.some((value) => restrictedItems.includes(value));

  return (
    <TextField
      label={label}
      placeholder={values.length > 0 ? "" : placeholder}
      variant="outlined"
      multiline
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onPaste={handlePaste}
      onBlur={convertTextToChips}
      disabled={disabled}
      error={anyDuplicatesExist || anyRestrictedValuesExist}
      helperText={
        anyDuplicatesExist ? "There are duplicate entries" : anyRestrictedValuesExist ? restrictedItemsHelperText : ""
      }
      slotProps={{
        input: {
          sx: {
            gap: 1.5,
            flexWrap: "wrap",
            "& .MuiOutlinedInput-input": {
              display: "flex",
              width: values.length === 0 ? "100%" : "auto",
              minWidth: "50%",
            },
          },
          startAdornment: values.map((value, index) => {
            const alreadyExists = values.filter((v) => v === value).length > 1;
            const restricted = restrictedItems && restrictedItems.includes(value);
            return (
              <Chip
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                color={alreadyExists || restricted ? "error" : "default"}
                variant="outlined"
                label={value}
                onDelete={() => handleDeleteChip(value, index)}
                disabled={disabled}
              />
            );
          }),
        },
      }}
    />
  );
};

export default ChipsMultilineInput;
